'use client';

import { MouseEvent } from 'react';
import { Box, Button, Card, Typography, useTheme } from '@mui/material';

interface MobileOutcomeRowProps {
  name: string;
  formattedVolume: string;
  percent: string;
  outcomeNameYes: string;
  yesPrice: string;
  outcomeNameNo: string;
  noPrice: string;
  isSelected: boolean;
  selectedSide?: 'yes' | 'no';
  onExpand: () => void;
  onBuyYes: (e: MouseEvent) => void;
  onBuyNo: (e: MouseEvent) => void;
}

export function MobileOutcomeRow({
  name,
  formattedVolume,
  percent,
  outcomeNameYes,
  yesPrice,
  outcomeNameNo,
  noPrice,
  isSelected,
  selectedSide,
  onExpand,
  onBuyYes,
  onBuyNo,
}: MobileOutcomeRowProps) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const isYesSelected = isSelected && selectedSide === 'yes';
  const isNoSelected = isSelected && selectedSide === 'no';

  return (
    <Card
      sx={{
        display: { xs: 'flex', md: 'none' },
        flexDirection: 'column',
        gap: 1.5,
        p: 2,
        mb: 1.5,
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        boxShadow: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      }}
      onClick={onExpand}
    >
      {/* Volume at top */}
      <Typography
        sx={{
          fontSize: '0.875rem',
          fontWeight: 500,
          color: 'text.secondary',
          mb: 0.5,
        }}
      >
        {formattedVolume} Vol.
      </Typography>

      {/* Outcome Name and Percentage Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5,
        }}
      >
        <Typography
          fontWeight={600}
          sx={{
            fontSize: '1rem',
            color: 'text.primary',
            lineHeight: 1.2,
          }}
        >
          {name}
        </Typography>
        <Typography
          fontWeight={700}
          sx={{
            fontSize: '1.125rem',
            color: 'text.primary',
            lineHeight: 1,
          }}
        >
          {percent}%
        </Typography>
      </Box>

      {/* Buy Yes and Buy No Buttons */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          fullWidth
          variant="contained"
          size="medium"
          sx={{
            bgcolor: isYesSelected ? 'success.main' : 'success.bg',
            color: isYesSelected ? '#ffffff' : 'success.dark',
            fontWeight: 600,
            fontSize: '0.875rem',
            py: 1.25,
            borderRadius: '8px',
            textTransform: 'none',
            boxShadow: isYesSelected ? '0 4px 12px rgba(34, 197, 94, 0.4)' : 'none',
            transform: isYesSelected ? 'translateY(-2px)' : 'none',
            '&:hover': {
              bgcolor: 'success.main',
              color: 'white',
              boxShadow: isYesSelected ? '0 6px 16px rgba(34, 197, 94, 0.5)' : 'none',
              transform: isYesSelected ? 'translateY(-3px)' : 'none',
            },
          }}
          onClick={onBuyYes}
        >
          {outcomeNameYes} {yesPrice}¢
        </Button>
        <Button
          fullWidth
          variant="contained"
          size="medium"
          sx={{
            bgcolor: isNoSelected ? 'error.main' : 'error.bg',
            color: isNoSelected ? '#ffffff' : 'error.dark',
            fontWeight: 600,
            fontSize: '0.875rem',
            py: 1.25,
            borderRadius: '8px',
            textTransform: 'none',
            boxShadow: isNoSelected ? '0 4px 12px rgba(239, 68, 68, 0.4)' : 'none',
            transform: isNoSelected ? 'translateY(-2px)' : 'none',
            '&:hover': {
              bgcolor: 'error.main',
              color: 'white',
              boxShadow: isNoSelected ? '0 6px 16px rgba(239, 68, 68, 0.5)' : 'none',
              transform: isNoSelected ? 'translateY(-3px)' : 'none',
            },
          }}
          onClick={onBuyNo}
        >
          {outcomeNameNo} {noPrice}¢
        </Button>
      </Box>
    </Card>
  );
}
